import { ceil, countDecimals, floor, round } from '.';
import { DEFAULT_LOAD_RANGE } from '../constants';
import { LoadType } from '../enums';
import { Range } from '../models';

export const calculateN0Range = (loadType: LoadType) => {
    let range: Range;

    switch (loadType) {
        case LoadType.QuadraticWithoutBreakaway:
        case LoadType.QuadraticWithBreakaway:
        case LoadType.Linear:
        case LoadType.NoLoad:
        case LoadType.Custom:
            range = DEFAULT_LOAD_RANGE;
            break;
        default:
            range = null as unknown as Range;
            break;
    }

    return range;
};

export const isDisabledByRange = (range: Range) => !range || round(range.min, 2) === round(range.max, 2);

export const calculateN1Range = (loadType: LoadType, nsyn: number, nw: number, nN: number, currentValue?: number) => {
    let range: Range;

    switch (loadType) {
        case LoadType.QuadraticWithBreakaway: {
            const min = 0.0001 * nsyn;
            const max = nw;

            range = calculateRange(min, max);
            break;
        }
        case LoadType.Custom: {
            const min = 0;
            const max = nN;

            range = calculateRange(min, max);
            break;
        }
        default:
            range = null as unknown as Range;
            break;
    }

    range = fixRange(range, currentValue);

    return range;
};

export const calculateN2Range = (loadType: LoadType, nN: number, n1: number, currentValue?: number) => {
    let range: Range;

    switch (loadType) {
        case LoadType.Custom:
            range = calculateRange(n1, nN);
            break;
        default:
            range = null as unknown as Range;
            break;
    }

    range = fixRange(range, currentValue);

    return range;
};

export const calculateN3Range = (loadType: LoadType, nN: number, n2: number, currentValue?: number) => {
    let range: Range;

    switch (loadType) {
        case LoadType.Custom:
            range = calculateRange(n2, nN);
            break;
        default:
            range = null as unknown as Range;
            break;
    }

    range = fixRange(range, currentValue);

    return range;
};

export const calculateN4Range = (loadType: LoadType, nsyn: number, nN: number, currentValue?: number) => {
    let range: Range;

    switch (loadType) {
        case LoadType.QuadraticWithoutBreakaway:
        case LoadType.Linear:
        case LoadType.NoLoad: {
            const min = 0.5 * nsyn;
            const max = nsyn;

            range = calculateRange(min, max);
            break;
        }
        case LoadType.QuadraticWithBreakaway: {
            const min = 0.75 * nsyn;
            const max = nsyn;

            range = calculateRange(min, max);
            break;
        }
        case LoadType.Custom:
            range = { min: nN, max: nN, step: 1 };
            break;
        default:
            range = null as unknown as Range;
            break;
    }

    range = fixRange(range, currentValue);

    return range;
};

export const calculateM0Range = (loadType: LoadType, ma: number, currentValue?: number) => {
    let range: Range;

    switch (loadType) {
        case LoadType.QuadraticWithoutBreakaway:
        case LoadType.NoLoad:
            range = { min: 0, max: 0, step: 1 };
            break;
        case LoadType.QuadraticWithBreakaway:
        case LoadType.Linear: {
            const min = 0.02 * ma;
            const max = 0.75 * ma;

            range = calculateRange(min, max);
            break;
        }
        case LoadType.Custom: {
            const min = 1;
            const max = 0.75 * ma;

            range = calculateRange(min, max);

            break;
        }
        default:
            range = null as unknown as Range;
            break;
    }

    range = fixRange(range, currentValue);

    if (loadType === LoadType.Custom) {
        range.min = 1;
    }

    return range;
};

export const calculateM1Range = (loadType: LoadType, ma: number, mn: number, currentValue?: number) => {
    let range: Range;

    switch (loadType) {
        case LoadType.QuadraticWithBreakaway:
            range = calculateRange(0.02 * ma, mn);
            break;
        case LoadType.Custom:
            range = calculateRange(0.05 * mn, mn);
            break;
        default:
            range = null as unknown as Range;
            break;
    }

    range = fixRange(range, currentValue);

    return range;
};

export const calculateM2Range = (loadType: LoadType, mn: number, currentValue?: number) => {
    let range: Range;

    switch (loadType) {
        case LoadType.Custom:
            range = calculateRange(0.05 * mn, mn);
            break;
        default:
            range = null as unknown as Range;
            break;
    }

    range = fixRange(range, currentValue);

    return range;
};

export const calculateM3Range = (loadType: LoadType, mn: number, currentValue?: number) => {
    let range: Range;

    switch (loadType) {
        case LoadType.Custom:
            range = calculateRange(0.05 * mn, mn);
            break;
        default:
            range = null as unknown as Range;
            break;
    }

    range = fixRange(range, currentValue);

    return range;
};

export const calculateM4Range = (loadType: LoadType, ma: number, mn: number, m0: number, m1: number, nN: number, n1: number, n4: number, nsyn: number, currentValue?: number) => {
    let range: Range;

    switch (loadType) {
        case LoadType.QuadraticWithoutBreakaway: {
            const min = 0.1 * mn;
            const max = n4 < nN ? E0Formula(mn, nN, n4) : E2Formula(mn, nN, n4, nsyn);

            range = calculateRange(min, max);
            break;
        }
        case LoadType.QuadraticWithBreakaway: {
            const min = 0.02 * ma;
            const max = n4 < nN ? E1Formula(mn, m1, nN, n1, n4) : E2Formula(mn, nN, n4, nsyn);

            range = calculateRange(min, max);
            break;
        }
        case LoadType.Constant: {
            const min = 0.1 * mn;
            const max = mn;

            range = calculateRange(min, max);
            break;
        }
        case LoadType.Linear: {
            const min = 0.1 * mn;
            const max = n4 < nN ? E3Formula(mn, m0, nN, n4) : E2Formula(mn, nN, n4, nsyn);

            range = calculateRange(min, max);
            break;
        }
        case LoadType.NoLoad: {
            const min = 0.02 * mn;
            const max = n4 < nN ? E0Formula(mn, nN, n4) : E2Formula(mn, nN, n4, nsyn);

            range = calculateRange(min, max);
            break;
        }
        case LoadType.Custom:
            range = { min: mn, max: mn, step: 1 };
            break;
    }

    range = fixRange(range, currentValue);

    return range;
};

export const fixRange = (range: Range, value?: number) => {
    const newRange = range;

    if (range && value != null || value != undefined) {       
        const valuePrecision = countDecimals(value);

        if (countDecimals(range.step) < valuePrecision) {
            newRange.step = Math.pow(10, -valuePrecision);
        }

        if (newRange.max < value) {
            newRange.max = value;
        }

        if (newRange.min > value) {
            newRange.min = value;
        }
    }

    return newRange;
};

const calculateRange = (min: number, max: number): Range => {
    const correctedMax = floor(max, max < 100 ? 1 : 0);
    const correctedMin = ceil(min, min < 100 ? 1 : 0);
    const step = correctedMax - correctedMin < 100 ? 0.1 : Number.isInteger(correctedMax) && Number.isInteger(correctedMin) ? 1 : 0.1;

    return {
        min: correctedMin,
        max: correctedMax < correctedMin ? correctedMin : correctedMax,
        step
    };
};

const E0Formula = (mn: number, nN: number, n4: number) => {
    return (mn / Math.pow(nN, 2)) * Math.pow(n4, 2);
};

const E1Formula = (mn: number, m1: number, nN: number, n1: number, n4: number) => {
    const a = ((mn - m1) / Math.pow(n1 - nN, 2)) * Math.pow(n4, 2);
    const b = ((2 * (mn - m1) * n1) / Math.pow(n1 - nN, 2)) * n4;
    const c = m1 + (((mn - m1) * Math.pow(n1, 2)) / Math.pow(nN - n1, 2));

    return a - b + c;
};

const E2Formula = (mn: number, nN: number, n4: number, nsyn: number) => {
    const a = (mn / (nsyn - nN)) * n4;
    const b = (mn * nsyn) / (nsyn - nN);

    return -a + b;
};

const E3Formula = (mn: number, m0: number, nN: number, n4: number) => {
    return (((mn - m0) / nN) * n4) + m0;
};

export const getClosestRangeRound = (value: number, min: number, step: number) => {
    const stepPrecision = countDecimals(step);
    let sliderValue = min;
    let temp: number;

    while ((temp = round(sliderValue + step, stepPrecision)) <= value) {
        sliderValue = temp;
    }

    return sliderValue;
};
